/**
 * CALENDARIO ANUAL — FASE 6.3 §B/§C
 *
 * Componente puro y reutilizable: pinta los 12 meses de un año y delega
 * TODA la interacción (qué es festivo, qué pasa al hacer click) en quien
 * lo usa. Lo consumen tanto `festivos-centro.tsx` (calendario real de un
 * centro) como `plantillas-calendario.tsx` (calendario de una plantilla,
 * antes de aplicarla) — misma pieza visual, dos orígenes de datos
 * distintos, para que un cambio de diseño no haya que hacerlo dos veces.
 *
 * Jerarquía visual deliberadamente corta (FASE 6.3 §C): normal, fin de
 * semana, festivo, hoy, seleccionado — cinco estados, no un mosaico.
 */
import type { ReactNode } from 'react';

export interface DiaCalendario {
  readonly fecha: string;
  readonly descripcion: string | null;
  readonly tipo: string;
  /** MANUAL | PLANTILLA — ausente en el calendario de una plantilla (no aplica). */
  readonly origen?: string;
  /** true en las excepciones nacionales: se ven, pero no se pueden tocar desde aquí. */
  readonly soloLectura?: boolean;
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DIAS_SEMANA_CORTOS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function isoDeSemana(fecha: Date): number {
  const dia = fecha.getUTCDay(); // 0=domingo..6=sábado
  return dia === 0 ? 7 : dia;
}

function aIso(anio: number, mes: number, dia: number): string {
  return `${String(anio).padStart(4, '0')}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

function diasDelMes(anio: number, mes: number): number {
  return new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
}

export function CalendarioAnual({
  anio,
  dias,
  seleccionado = null,
  onClickDia,
  hoy = new Date().toISOString().slice(0, 10),
}: {
  anio: number;
  dias: ReadonlyMap<string, DiaCalendario>;
  seleccionado?: string | null;
  /**
   * Se llama con cualquier click, festivo o no — este componente no
   * decide qué significa un click; eso lo decide quien lo usa (crear,
   * seleccionar, o no hacer nada si no puede editar). Sin esta prop, los
   * días se pintan pero no son interactivos (calendario de solo lectura).
   */
  onClickDia?: (fecha: string) => void;
  /** AAAA-MM-DD — inyectable para pruebas; por defecto el día real. */
  hoy?: string;
}): ReactNode {
  return (
    <div className="calendario-anual">
      {MESES.map((nombre, mes) => (
        <MesCalendario
          key={mes}
          nombre={nombre}
          anio={anio}
          mes={mes}
          dias={dias}
          seleccionado={seleccionado}
          onClickDia={onClickDia}
          hoy={hoy}
        />
      ))}
    </div>
  );
}

function MesCalendario({
  nombre,
  anio,
  mes,
  dias,
  seleccionado,
  onClickDia,
  hoy,
}: {
  nombre: string;
  anio: number;
  mes: number;
  dias: ReadonlyMap<string, DiaCalendario>;
  seleccionado: string | null;
  onClickDia?: (fecha: string) => void;
  hoy: string;
}): ReactNode {
  const total = diasDelMes(anio, mes);
  const primerIso = isoDeSemana(new Date(Date.UTC(anio, mes, 1)));
  //  Huecos antes del día 1 para que la cuadrícula empiece siempre en
  //  lunes, como el resto de la aplicación (L M X J V S D).
  const huecos = primerIso - 1;

  return (
    <div className="calendario-mes">
      <div className="calendario-mes-titulo">{nombre}</div>
      <div className="calendario-mes-cabecera">
        {DIAS_SEMANA_CORTOS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="calendario-mes-dias">
        {Array.from({ length: huecos }, (_, i) => (
          <span key={`hueco-${String(i)}`} className="calendario-dia fuera-mes" aria-hidden="true" />
        ))}
        {Array.from({ length: total }, (_, i) => {
          const dia = i + 1;
          const fecha = aIso(anio, mes, dia);
          const info = dias.get(fecha);
          const finde = isoDeSemana(new Date(Date.UTC(anio, mes, dia))) >= 6;
          const esHoy = fecha === hoy;
          const esSeleccionado = fecha === seleccionado;
          const clases = ['calendario-dia'];
          if (finde) clases.push('finde');
          if (info !== undefined) clases.push('festivo');
          if (esHoy) clases.push('hoy');
          if (esSeleccionado) clases.push('seleccionado');

          const titulo = info === undefined
            ? undefined
            : `${info.descripcion ?? ''}${info.origen !== undefined ? ` (${info.origen === 'PLANTILLA' ? 'plantilla' : 'manual'})` : ''}`.trim();

          if (onClickDia === undefined) {
            return (
              <span key={fecha} className={clases.join(' ')} title={titulo}>
                {dia}
              </span>
            );
          }

          return (
            <button
              key={fecha}
              type="button"
              className={clases.join(' ')}
              title={titulo}
              aria-pressed={esSeleccionado}
              aria-label={`${String(dia)} de ${nombre}${info !== undefined ? ', festivo' : ''}`}
              onClick={() => onClickDia(fecha)}
            >
              {dia}
            </button>
          );
        })}
      </div>
    </div>
  );
}
